import {
  createListenerMiddleware,
  isFulfilled,
  UnknownAction,
} from '@reduxjs/toolkit';

import {
  mergeReadableResources,
  mergeResourceTags,
} from '@/shared/features/permissions/store/permissions.slice';

// Harvests readable resources from the responses each app fetches organically (each
// backend-filtered to what the user can read) — no prefetch. Matched by the query's
// `endpointName` rather than importing the api objects, so this globally-wired listener stays
// decoupled from the ~12 feature apis and the store remains mock-friendly in tests.
//   `readAll*`              → id + groupId
//   `ReadLinkedTagsForAsset` → the asset's tags (for `entity:tag:<name>` scopes; harvested now,
//                              consumed once a tag-scoped feature migrates to `can()`)
// The bulk `ReadAllFullTags` reverse-map is intentionally not used — per-asset linked tags are
// the single simple tag source.
export const RESOURCE_HARVESTERS: Record<
  string,
  { entity: string; rootField: string; idField: string; groupIdField?: string }
> = {
  ReadAllDevices: {
    entity: 'device',
    rootField: 'readAllDevices',
    idField: 'deviceId',
  },
  ReadDevicesForList: {
    entity: 'device',
    rootField: 'readAllDevices',
    idField: 'deviceId',
  },
  ReadDevicesForSelect: {
    entity: 'device',
    rootField: 'readAllDevices',
    idField: 'deviceId',
  },
  ReadAllGateways: {
    entity: 'gateway',
    rootField: 'readAllGateways',
    idField: 'gatewayId',
  },
  ReadGatewaysForList: {
    entity: 'gateway',
    rootField: 'readAllGateways',
    idField: 'gatewayId',
  },
  ReadAllProjects: {
    entity: 'project',
    rootField: 'readAllProjects',
    idField: 'projectId',
  },
  ReadProjectsForSelect: {
    entity: 'project',
    rootField: 'readAllProjects',
    idField: 'projectId',
  },
  ReadProjectsForList: {
    entity: 'project',
    rootField: 'readAllProjects',
    idField: 'projectId',
  },
  ReadAllDocuments: {
    entity: 'document',
    rootField: 'readAllDocuments',
    idField: 'documentId',
  },
  ReadAllVaults: {
    entity: 'vault',
    rootField: 'readAllVaults',
    idField: 'vaultId',
  },
  ReadAllLicenses: {
    entity: 'license',
    rootField: 'readAllLicenses',
    idField: 'licenseId',
  },
  ReadAllWorkstations: {
    entity: 'workstation',
    rootField: 'readAllWorkstationsForUser',
    idField: 'workstationId',
  },
  ReadAllPipelines: {
    entity: 'pipeline',
    rootField: 'readAllPipelineConfigurations',
    idField: 'pipelineConfigurationId',
  },
  ReadAllIntegrations: {
    entity: 'integration',
    rootField: 'readAllIntegrations',
    idField: 'integrationId',
  },
  ReadAllWebhookConfigurations: {
    entity: 'webhook',
    rootField: 'readAllWebhookConfigurations',
    idField: 'webhookConfigurationId',
  },
  // A group's containing group (for `resourceGroup:group:X` scopes) is its parent.
  ReadAllResourceGroups: {
    entity: 'resourceGroup',
    rootField: 'readAllResourceGroups',
    idField: 'groupId',
    groupIdField: 'parentGroupId',
  },
  ReadResourceGroupsToBuildTree: {
    entity: 'resourceGroup',
    rootField: 'readAllResourceGroups',
    idField: 'groupId',
    groupIdField: 'parentGroupId',
  },
  ReadSubGroupsForResourceGroup: {
    entity: 'resourceGroup',
    rootField: 'readSubGroupsForResourceGroup',
    idField: 'groupId',
    groupIdField: 'parentGroupId',
  },
  ReadResourceGroupNamesForRoleRow: {
    entity: 'resourceGroup',
    rootField: 'readResourceGroupsByIds',
    idField: 'groupId',
    groupIdField: 'parentGroupId',
  },
  // Users and roles share the `ident` action prefix (disjoint id spaces).
  ReadAllUsers: {
    entity: 'ident',
    rootField: 'readAllUsers',
    idField: 'userId',
  },
  ReadAllRoles: {
    entity: 'ident',
    rootField: 'readAllUserRoles',
    idField: 'userRoleId',
  },
  ReadUsersForRole: {
    entity: 'ident',
    rootField: 'readUsersForUserRole',
    idField: 'userId',
  },
  ReadAllSuperUsers: {
    entity: 'backofficeUser',
    rootField: 'readAllUsers',
    idField: 'userId',
  },
  ReadAllSuperUserRoles: {
    entity: 'backofficeUserRole',
    rootField: 'readAllUserRoles',
    idField: 'userRoleId',
  },
  ReadSuperUsersForUserRole: {
    entity: 'backofficeUser',
    rootField: 'readUsersForUserRole',
    idField: 'userId',
  },
  ReadTenantsForSuperTenant: {
    entity: 'backofficeTenant',
    rootField: 'readTenantsForSuperTenant',
    idField: 'tenantId',
  },
  ReadBackofficeResourceGroupsToBuildTree: {
    entity: 'backofficeGroup',
    rootField: 'readAllResourceGroups',
    idField: 'groupId',
    groupIdField: 'parentGroupId',
  },
  ReadAllBackofficeResourceGroups: {
    entity: 'backofficeGroup',
    rootField: 'readAllResourceGroups',
    idField: 'groupId',
    groupIdField: 'parentGroupId',
  },
  ReadSubGroupsForBackofficeResourceGroup: {
    entity: 'backofficeGroup',
    rootField: 'readSubGroupsForResourceGroup',
    idField: 'groupId',
    groupIdField: 'parentGroupId',
  },
  ReadBackofficeResourceGroupNamesForRoleRow: {
    entity: 'backofficeGroup',
    rootField: 'readResourceGroupsByIds',
    idField: 'groupId',
    groupIdField: 'parentGroupId',
  },
};

// Single-resource reads (`Read<Entity>`) return one object, not a connection. Harvesting them
// too means detail/deep-linked pages populate the store, so group/tag-scoped checks resolve there
// even when the resource was never in a fetched list.
const SINGLE_READ_HARVESTERS: Record<
  string,
  { entity: string; rootField: string; idField: string; groupIdField?: string }
> = {
  ReadUser: { entity: 'ident', rootField: 'readUser', idField: 'userId' },
  ReadRole: {
    entity: 'ident',
    rootField: 'readUserRole',
    idField: 'userRoleId',
  },
  ReadResourceGroupById: {
    entity: 'resourceGroup',
    rootField: 'readResourceGroup',
    idField: 'groupId',
    groupIdField: 'parentGroupId',
  },
  ReadWorkstation: {
    entity: 'workstation',
    rootField: 'readWorkstation',
    idField: 'workstationId',
  },
  ReadProject: {
    entity: 'project',
    rootField: 'readProject',
    idField: 'projectId',
  },
  ReadSuperUser: {
    entity: 'backofficeUser',
    rootField: 'readUser',
    idField: 'userId',
  },
  ReadSuperUserRole: {
    entity: 'backofficeUserRole',
    rootField: 'readUserRole',
    idField: 'userRoleId',
  },
  ReadTenantForSuperTenant: {
    entity: 'backofficeTenant',
    rootField: 'readTenant',
    idField: 'tenantId',
  },
  ReadTenantForUpdate: {
    entity: 'backofficeTenant',
    rootField: 'readTenant',
    idField: 'tenantId',
  },
  ReadTenantFeatureFlags: {
    entity: 'backofficeTenant',
    rootField: 'readTenant',
    idField: 'tenantId',
  },
  ReadBackofficeResourceGroupById: {
    entity: 'backofficeGroup',
    rootField: 'readResourceGroup',
    idField: 'groupId',
    groupIdField: 'parentGroupId',
  },
  ReadBackofficeGroupResourceCounts: {
    entity: 'backofficeGroup',
    rootField: 'readBackofficeResourceGroup',
    idField: 'groupId',
    groupIdField: 'parentGroupId',
  },
};

export const TAG_HARVESTER = {
  endpointName: 'ReadLinkedTagsForAsset',
  rootField: 'readLinkedTagsForAsset',
  nameField: 'name',
} as const;

const BATCHED_TAG_HARVESTER = {
  endpointName: 'ReadLinkedTagsForAssets',
  rootField: 'readLinkedTagsForAssets',
  assetIdField: 'assetId',
  tagsField: 'tags',
  nameField: 'name',
} as const;

const ASSET_TYPE_TO_ENTITY: Record<string, string> = {
  DEVICE: 'device',
  GATEWAY: 'gateway',
  PROJECT: 'project',
  DOCUMENT: 'document',
  LICENSE: 'license',
  VAULT: 'vault',
  PIPELINE: 'pipeline',
  WORKSTATION: 'workstation',
};

type TRecord = Record<string, unknown>;

const asRecord = (value: unknown): TRecord | undefined =>
  value && typeof value === 'object' ? (value as TRecord) : undefined;

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

// Reads `{ items: [...] }` connection shapes, keeping only object items.
const readItems = (connection: unknown): TRecord[] => {
  const items = asRecord(connection)?.items;

  return Array.isArray(items)
    ? items.filter((item): item is TRecord => Boolean(asRecord(item)))
    : [];
};

const readRecords = (records: unknown): TRecord[] =>
  Array.isArray(records)
    ? records.filter((item): item is TRecord => Boolean(asRecord(item)))
    : [];

const getEndpointName = (action: UnknownAction): string | undefined =>
  asString(
    (action as { meta?: { arg?: { endpointName?: unknown } } }).meta?.arg
      ?.endpointName,
  );

const toResourceItem = (
  record: TRecord,
  idField: string,
  groupIdField: string,
): { id: string; groupId: string | null } | undefined => {
  const id = asString(record[idField]);

  return id
    ? { id, groupId: asString(record[groupIdField]) ?? null }
    : undefined;
};

const readRelatedIdentResources = (
  name: string,
  payload: TRecord,
): { id: string; groupId: string | null }[] => {
  const items: { id: string; groupId: string | null }[] = [];
  const addUser = (record: TRecord) => {
    const item = toResourceItem(record, 'userId', 'groupId');
    if (item) {
      items.push(item);
    }
  };
  const addRole = (record: TRecord) => {
    const item = toResourceItem(record, 'userRoleId', 'groupId');
    if (item) {
      items.push(item);
    }
  };
  const collectFromUser = (user: TRecord) => {
    addUser(user);
    readItems(user['userRoles']).forEach(addRole);
  };
  const collectFromRole = (role: TRecord) => {
    addRole(role);
    readItems(role['users']).forEach(addUser);
  };

  if (name === 'ReadAllUsers') {
    readItems(payload['readAllUsers']).forEach(collectFromUser);
  } else if (name === 'ReadUsersForRole') {
    readItems(payload['readUsersForUserRole']).forEach(collectFromUser);
  } else if (name === 'ReadUser') {
    const user = asRecord(payload['readUser']);
    if (user) {
      collectFromUser(user);
    }
  } else if (name === 'ReadAllRoles') {
    readItems(payload['readAllUserRoles']).forEach(collectFromRole);
  } else if (name === 'ReadRole') {
    const role = asRecord(payload['readUserRole']);
    if (role) {
      collectFromRole(role);
    }
  }

  return items;
};

const readRelatedBackofficeResources = (
  name: string,
  payload: TRecord,
): Record<string, { id: string; groupId: string | null }[]> => {
  const users: { id: string; groupId: string | null }[] = [];
  const roles: { id: string; groupId: string | null }[] = [];
  const addUser = (record: TRecord) => {
    const item = toResourceItem(record, 'userId', 'groupId');
    if (item) {
      users.push(item);
    }
  };
  const addRole = (record: TRecord) => {
    const item = toResourceItem(record, 'userRoleId', 'groupId');
    if (item) {
      roles.push(item);
    }
  };
  // A user's roles arrive as OwnSuperUserRole (no member list), same as ident.
  const collectFromUser = (user: TRecord) => {
    addUser(user);
    readItems(user['userRoles']).forEach(addRole);
  };
  const collectFromRole = (role: TRecord) => {
    addRole(role);
    readItems(role['users']).forEach(addUser);
  };

  if (name === 'ReadAllSuperUsers') {
    readItems(payload['readAllUsers']).forEach(collectFromUser);
  } else if (name === 'ReadSuperUsersForUserRole') {
    readItems(payload['readUsersForUserRole']).forEach(collectFromUser);
  } else if (name === 'ReadSuperUser') {
    const user = asRecord(payload['readUser']);
    if (user) {
      collectFromUser(user);
    }
  } else if (name === 'ReadAllSuperUserRoles') {
    readItems(payload['readAllUserRoles']).forEach(collectFromRole);
  } else if (name === 'ReadSuperUserRole') {
    const role = asRecord(payload['readUserRole']);
    if (role) {
      collectFromRole(role);
    }
  }

  return { backofficeUser: users, backofficeUserRole: roles };
};

export const permissionsListener = createListenerMiddleware();

permissionsListener.startListening({
  predicate: (action) => {
    if (!isFulfilled(action)) {
      return false;
    }

    const name = getEndpointName(action);

    return Boolean(
      name &&
      (name in RESOURCE_HARVESTERS ||
        name in SINGLE_READ_HARVESTERS ||
        name === TAG_HARVESTER.endpointName ||
        name === BATCHED_TAG_HARVESTER.endpointName),
    );
  },
  effect: (action, { dispatch }) => {
    const name = getEndpointName(action);
    const payload = asRecord((action as { payload?: unknown }).payload);

    if (!name || !payload) {
      return;
    }

    const mergeRelatedIdentResources = () => {
      const items = readRelatedIdentResources(name, payload);

      if (items.length) {
        dispatch(mergeReadableResources({ entity: 'ident', items }));
      }
    };
    const mergeRelatedBackofficeResources = () => {
      Object.entries(readRelatedBackofficeResources(name, payload)).forEach(
        ([entity, items]) => {
          if (items.length) {
            dispatch(mergeReadableResources({ entity, items }));
          }
        },
      );
    };

    const listHarvester = RESOURCE_HARVESTERS[name];

    if (listHarvester) {
      const groupIdField = listHarvester.groupIdField ?? 'groupId';
      const items = readItems(payload[listHarvester.rootField])
        .map((item) =>
          toResourceItem(item, listHarvester.idField, groupIdField),
        )
        .filter((item): item is { id: string; groupId: string | null } =>
          Boolean(item),
        );

      if (items.length) {
        dispatch(
          mergeReadableResources({ entity: listHarvester.entity, items }),
        );
      }

      mergeRelatedIdentResources();
      mergeRelatedBackofficeResources();

      return;
    }

    const singleHarvester = SINGLE_READ_HARVESTERS[name];

    if (singleHarvester) {
      const record = asRecord(payload[singleHarvester.rootField]);
      const item =
        record &&
        toResourceItem(
          record,
          singleHarvester.idField,
          singleHarvester.groupIdField ?? 'groupId',
        );

      if (item) {
        dispatch(
          mergeReadableResources({
            entity: singleHarvester.entity,
            items: [item],
          }),
        );
      }

      mergeRelatedIdentResources();
      mergeRelatedBackofficeResources();

      return;
    }

    const originalArgs = asRecord(
      (action as { meta?: { arg?: { originalArgs?: unknown } } }).meta?.arg
        ?.originalArgs,
    );
    const entity =
      ASSET_TYPE_TO_ENTITY[asString(originalArgs?.assetType) ?? ''];

    if (!entity) {
      return;
    }

    if (name === BATCHED_TAG_HARVESTER.endpointName) {
      const rootValue = payload[BATCHED_TAG_HARVESTER.rootField];
      const batchedTagRecords = Array.isArray(rootValue)
        ? readRecords(rootValue)
        : readItems(rootValue);

      const items = batchedTagRecords
        .map((record) => {
          const id = asString(record[BATCHED_TAG_HARVESTER.assetIdField]);
          const tagsValue = record[BATCHED_TAG_HARVESTER.tagsField];
          const tagRecords = Array.isArray(tagsValue)
            ? readRecords(tagsValue)
            : readItems(tagsValue);
          const tags = tagRecords
            .map((tag) => asString(tag[BATCHED_TAG_HARVESTER.nameField]))
            .filter((tagName): tagName is string => Boolean(tagName));

          return id ? { id, tags } : undefined;
        })
        .filter((item): item is { id: string; tags: string[] } =>
          Boolean(item),
        );

      if (items.length) {
        dispatch(mergeResourceTags({ entity, items }));
      }

      return;
    }

    const assetId = asString(originalArgs?.assetId);

    if (!assetId) {
      return;
    }

    const tags = readItems(payload[TAG_HARVESTER.rootField])
      .map((tag) => asString(tag[TAG_HARVESTER.nameField]))
      .filter((tagName): tagName is string => Boolean(tagName));

    dispatch(mergeResourceTags({ entity, items: [{ id: assetId, tags }] }));
  },
});
